import { SearchQueryLabel } from "~/features/search/components/common/SearchQueryLabel";
import ContentResults from "~/features/search/components/ContentResults";
import { useTranslation } from "react-i18next";
import { useOnMount } from "~/common/hooks/useOnMount";
import { searchResultsMounted } from "~/features/search/redux/actions";
import { useDispatch, useSelector } from "react-redux";
import {
  selectContentResultAmount,
  selectCurrentPage,
  selectIsInFlight,
  selectIsInit,
  selectProductResultAmount,
  selectSearchQuery,
  selectTechnicalInfo,
} from "~/features/search/redux/searchSelectors";
import { type CSSProperties, type ReactNode, useEffect, useRef } from "react";
import { trackZeroResultTracking } from "~/app/analytics/ga/ga";
import { useTabHashLogic } from "~/features/search/hooks/useTabHashLogic";
import ProductResults from "~/features/search/components/ProductResults/ProductResults";
import { SearchPageType } from "~/features/search/models/SearchPageType";
import { SearchPageTypeProvider } from "~/features/search/context/SearchPageTypeContext";
import { ActiveSalesCampaignCodesProvider } from "~/features/salesCampaign/hooks/useActiveSalesCampaignCodes";
import { RecentlyViewedProducts } from "~/features/RecentlyViewedProducts/RecentlyViewedProducts";
import { SRPCampaignRecommendations } from "~/features/saleRecommendations/components/SRPCampaignRecommendations";
import { SRPTopsellerRecommendations } from "~/features/saleRecommendations/components/TopsellerRecommendations";
import { FCCGapProductRecommendation } from "~/features/saleRecommendations/components/SchaefflerFCCGapProductsRecommendationComponent";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { Tab } from "~/common/mui/Tabs/Tab";
import { Tabs } from "~/common/mui/Tabs/Tabs";
import { Grid, Skeleton, Stack } from "~/common/mui";
import { ReduxFilterSection } from "~/features/search/components/Filters/ReduxFilterSection";
import { PurchasePlaceProvider } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import { MoreInformationTab } from "~/features/productDetails/components/PdpTabs/MoreInformationTab";
import type { TabDefinition } from "~/features/search/hooks/useSearchPageTabs";
import TopicOutlinedIcon from "@mui/icons-material/TopicOutlined";
import { Filter } from "~/features/search/models/filterConfigModels";
import { selectVisibleFilters } from "~/features/search/redux/filterConfigSelectors";
import { isComingFromSeriesTreeClick } from "~/features/search/redux/seriesTreeSelectors";
import { useSelectAppliedFilters } from "~/features/search/components/Filters/components/AppliedFilters/useSelectAppliedFilters";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import {
  FULLBLEED_LEFT_SIZE,
  GRID_CONTAINER_FULLBLEED_STYLE,
} from "~/features/search/components/ProductResults/fullbleedUtils";
import { useSRPResultsViewMode } from "~/features/search/components/ProductResults/useSRPResultsViewMode";
import { SearchDisplayMode } from "~/features/search/models/models";
import { useIsMinDesktopSizeM } from "~/common/hooks/useMediaQuery";
import { useFullbleed } from "~/features/search/components/ProductResults/useFullbleed";
import { FiltersContainer } from "../ProductListPage/ProductListPage";
import BallBearingIcon from "~/common/mui/icons/BallBearing";
import type { RecommendationPlacement } from "~/features/search/models/RecommendationPlacement";
import { setRecommendationPlacement } from "~/features/search/redux/actions";

const SrpTabs: Record<string, TabDefinition> = {
  Search: {
    id: "Products",
    gaName: "Product",
    icon: <BallBearingIcon sx={{ fontSize: 24 }} />,
    label: ({ t, productAmount }) =>
      t("page.plp.tab.search") + " (" + NF.getDefaultFormatter().format(productAmount) + ")",
    showFilters: true,
  },
  MoreInformation: {
    id: "More Information",
    gaName: "More Information",
    icon: <MenuBookIcon />,
    label: ({ t }) => t("page.srp.tab.moreInfo"),
    showFilters: false,
  },
  RelatedContent: {
    id: "Related Content",
    gaName: "Related Content",
    icon: <TopicOutlinedIcon />,
    label: ({ t, contentAmount }) => t("searchPage.tabs.related") + " (" + contentAmount + ")",
    showFilters: false,
  },
} as const;

interface Props {
  activeSalesCampaignCodes: string[];
  recommendationPlacement?: RecommendationPlacement;
}

export const SearchPage = ({ activeSalesCampaignCodes, recommendationPlacement }: Props) => {
  const { t } = useTranslation();

  const isMinDesktopSizeM = useIsMinDesktopSizeM();

  const [activeTab, setActiveTab] = useTabHashLogic(SrpTabs, {
    defaultTab: SrpTabs.Search.id,
    tabContainerId: "tabsSrp",
    scrollOnPageLoad: true,
  });

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setRecommendationPlacement(recommendationPlacement));
  }, [dispatch, recommendationPlacement]);

  useOnMount(() => {
    dispatch(searchResultsMounted());
  });

  const technicalInformation = useSelector(selectTechnicalInfo);
  const haveTechnicalInfo = !!technicalInformation?.columns?.length;
  const productAmount = useSelector(selectProductResultAmount);
  const currentPage = useSelector(selectCurrentPage);
  const contentAmount = useSelector(selectContentResultAmount);
  const searchText = useSelector(selectSearchQuery);
  const inFlight = useSelector(selectIsInFlight);
  const init = useSelector(selectIsInit);
  const appliedFilters = useSelectAppliedFilters();
  const isComingFromSeriesTree = useSelector(isComingFromSeriesTreeClick);
  let filterConfig = useSelector(selectVisibleFilters);
  if (isComingFromSeriesTree) {
    filterConfig = filterConfig.filter((item) => item.type !== Filter.TAXONOMY);
  }

  const [displayMode] = useSRPResultsViewMode();
  const enableTechnicalViewFullBleed = displayMode === SearchDisplayMode.TABULAR && activeTab === SrpTabs.Search.id;

  useEffect(() => {
    if (!init || inFlight) {
      return;
    }
    if (
      (activeTab === SrpTabs.MoreInformation.id && !haveTechnicalInfo) ||
      (activeTab === SrpTabs.RelatedContent.id && contentAmount <= 0)
    ) {
      setActiveTab(SrpTabs.Search.id);
    }

    if (productAmount === 0 || contentAmount === 0) {
      trackZeroResultTracking(searchText, productAmount, contentAmount);
    }
  }, [productAmount, contentAmount, inFlight, init]);

  const { component, position } =
    getRecommendation(
      inFlight ? 20 : productAmount,
      currentPage,
      recommendationPlacement,
    ) ?? {};

  const tabVisibility: Record<string, boolean> = {
    [SrpTabs.Search.id]: true,
    [SrpTabs.MoreInformation.id]: haveTechnicalInfo,
    [SrpTabs.RelatedContent.id]: contentAmount > 0,
  };

  const tabComponent: Record<string, ReactNode> = {
    [SrpTabs.Search.id]: <ProductResults recommendationPosition={position} recommendationComponent={component} />,
    [SrpTabs.MoreInformation.id]: <MoreInformationTab data={technicalInformation} />,
    [SrpTabs.RelatedContent.id]: <ContentResults />,
  };

  const labelProps = {
    t,
    productAmount,
    contentAmount,
  };

  const getTabProps = (tab: TabDefinition) => ({
    value: tab.id,
    icon: tab.icon,
    label: tab.label(labelProps),
  });

  const tabs = [SrpTabs.Search, SrpTabs.MoreInformation, SrpTabs.RelatedContent].filter((tab) => tabVisibility[tab.id]);
  const showTabs = tabs.length > 1;

  let showFilters = tabs.find((tab) => tab.id === activeTab)?.showFilters ?? false;
  const noResults = init && productAmount === 0 && appliedFilters.length === 0 && filterConfig.length === 0;

  if (enableTechnicalViewFullBleed && !isMinDesktopSizeM) {
    showFilters = false;
  }

  const containerRef = useRef<HTMLDivElement>(null);
  const bleed = useFullbleed(containerRef);
  const style = { [FULLBLEED_LEFT_SIZE]: `${bleed}px` } as CSSProperties;

  return (
    <PurchasePlaceProvider value={"SRP"}>
      <ActiveSalesCampaignCodesProvider value={activeSalesCampaignCodes}>
        <SearchPageTypeProvider overrideType={SearchPageType.SEARCH}>
          <Stack spacing={{ phone: 2, "desktop-s": 3 }} ref={containerRef}>
            {init && <SearchQueryLabel query={searchText} id={"resultsHeader"} />}

            {!init && (
              <>
                <Skeleton variant="rounded" height={40} width={"40%"} />
                <Stack spacing={1} direction={"row"}>
                  <Skeleton variant="rounded" height={64} width={"100%"} sx={{ flex: 1 }} />
                  <Skeleton variant="rounded" height={64} width={"100%"} sx={{ flex: 1 }} />
                  <Skeleton variant="rounded" height={64} width={"100%"} sx={{ flex: 1 }} />
                </Stack>
              </>
            )}

            {showTabs && (
              <Tabs
                id={"tabsSrp"}
                onChange={(e, val) => setActiveTab(val, true)}
                value={activeTab}
                fullWidthIndicator={false}
                variant="fullWidth"
              >
                {tabs.map((tab) => (
                  <Tab {...getTabProps(tab)} hidden={!tabVisibility[tab.id]} />
                ))}
              </Tabs>
            )}

            <Grid
              container
              style={style}
              sx={{
                ...(enableTechnicalViewFullBleed ? GRID_CONTAINER_FULLBLEED_STYLE : undefined),
                alignItems: "flex-start",
              }}
            >
              <FiltersContainer
                sx={{ display: { phone: "none", "desktop-s": noResults ? "none" : "block" } }}
                hidden={!showFilters}
                size={{
                  "desktop-s": 3,
                }}
              >
                <ReduxFilterSection loading={!init} />
              </FiltersContainer>

              {tabs.map((tab) => (
                <Grid
                  key={tab.id}
                  hidden={activeTab !== tab.id}
                  size={{
                    phone: 12,
                    "desktop-s": tab.showFilters && !noResults ? 9 : 12,
                  }}
                >
                  {tabComponent[tab.id]}
                </Grid>
              ))}
            </Grid>
          </Stack>

          {/** To display FCC product recommendation in zero search results page */}
          {!inFlight && productAmount === 0 && <FCCGapProductRecommendation />}
          <RecentlyViewedProducts />
        </SearchPageTypeProvider>
      </ActiveSalesCampaignCodesProvider>
    </PurchasePlaceProvider>
  );
};

export interface SearchPageProps {
  recommendationPlacement?: RecommendationPlacement;
}

export function createSearchPage({ recommendationPlacement }: SearchPageProps = {}) {
  // bindHistoryListener(store);

  const activeSalesCampaignCodes = window.SCHA.activeSalesCampaigns.map((campaign) => campaign.code);

  return (
    <SearchPage
      activeSalesCampaignCodes={activeSalesCampaignCodes}
      recommendationPlacement={recommendationPlacement}
    />
  );
}

export function getRecommendation(
  resultAmount: number,
  resultPage: number,
  recommendationPlacement?: RecommendationPlacement,
) {
  if (!recommendationPlacement || resultPage !== 0) {
    return null;
  }

  if (recommendationPlacement.type === "SALES_CAMPAIGN") {
    return {
      position: recommendationPlacement.position,
      component: <SRPCampaignRecommendations key="similar-sales" />,
    };
  }

  if (recommendationPlacement.type === "TOPSELLER" && resultAmount >= 4) {
    return {
      position: recommendationPlacement.position,
      component: <SRPTopsellerRecommendations key="similar-topseller" />,
    };
  }

  return null;
}
